import { injectable, decorate, inject } from 'inversify';
import 'reflect-metadata';
import { format } from 'date-fns';
import { SERVICE_IDENTIFIER } from 'constants/identifiers';

@injectable()
class ConstructionService {
  constructor(apiService) {
    this.apiService = apiService;
  }

  getStatuses = async () => {
    const result = await this.apiService.get('/construction_cards/statuses');

    return result.data.data.items;
  };

  async getConstructionCardList(params, abortSignal) {
    const result = await this.apiService.get(
      '/construction_cards/list',
      params,
      { signal: abortSignal },
    );

    return result.data.data.items;
  }

  getConstructions = async (params) => {
    const result = await this.apiService.get('/constructions', params);

    return result.data.data.items;
  };

  getContractors = async (params) => {
    const result = await this.apiService.get('/customers_list', params);

    return result.data.data.items;
  };

  getObjectManager = async (objectManagerId) => {
    const result = await this.apiService.get(
      `/construction_cards/manager/${objectManagerId}`,
    );

    return result.data.data.items;
  };

  postNeedHelp = async (constructionCardId) => {
    const result = await this.apiService.post(
      `/construction_cards/${constructionCardId}/need_help`,
    );

    return result.data.data.items[0];
  };

  postAcceptCard = async (constructionCardId) => {
    const result = await this.apiService.post(
      `/construction_cards/${constructionCardId}/accept_card`,
    );

    return result.data.data.items[0];
  };

  postCardDidntMatch = async (constructionCardId) => {
    const result = await this.apiService.post(
      `/construction_cards/${constructionCardId}/card_didnt_match`,
    );

    return result.data.data.items[0];
  };

  getManagers = async (params) => {
    const result = await this.apiService.get(
      `/construction_cards/managers`,
      params,
    );

    return result.data.data.items;
  };

  getUnits = async (objectManagerId) => {
    const result = await this.apiService.get(
      `/construction_cards/unit/${objectManagerId}`,
    );

    return result.data.data.items;
  };

  async createConstructionCard(data) {
    const {
      price,
      contractor,
      construction,
      matgroup,
      purchaser,
      objectManager,
      customManager,
      firstShipmentPlanDate,
      ...other
    } = data;

    const newPrice = Number(price);
    const newPurchaser = purchaser[0].contactId;
    const newContractor = contractor[0].id;
    const newConstruction = construction[0].id;
    const newMatgroup = matgroup[0].id;
    const newCustomManager = customManager[0]?.id;
    const newFirstShipmentPlanDate = format(
      firstShipmentPlanDate,
      'yyyy-MM-dd',
    );

    const newData = {
      price: newPrice,
      purchaser: newPurchaser,
      matgroup: newMatgroup,
      construction: newConstruction,
      contractor: newContractor,
      customManager: newCustomManager,
      firstShipmentPlanDate: newFirstShipmentPlanDate,
      ...other,
    };

    const result = await this.apiService.post(
      '/construction_cards/new',
      newData,
    );

    return result.data.data.items;
  }

  async updateConstructionCard(constructionCardId, data) {
    const {
      price,
      contractor,
      construction,
      matgroup,
      purchaser,
      objectManager,
      customManager,
      firstShipmentPlanDate,
      ...other
    } = data;

    const newPrice = Number(price);
    const newPurchaser = purchaser[0].contactId;
    const newContractor = contractor[0].id;
    const newConstruction = construction[0].id;
    const newMatgroup = matgroup[0].id;
    const newCustomManager = customManager[0].id;
    const newFirstShipmentPlanDate = firstShipmentPlanDate
      ? format(firstShipmentPlanDate, 'yyyy-MM-dd')
      : null;

    const newData = {
      price: newPrice,
      purchaser: newPurchaser,
      matgroup: newMatgroup,
      construction: newConstruction,
      contractor: newContractor,
      customManager: newCustomManager,
      firstShipmentPlanDate: newFirstShipmentPlanDate,
      ...other,
    };

    const result = await this.apiService.put(
      `/construction_cards/${constructionCardId}`,
      newData,
    );

    return result.data.data.items;
  }

  getConstructionCardItem = async (constructionCardId) => {
    const result = await this.apiService.get(
      `/construction_cards/${constructionCardId}`,
    );

    return result.data.data.items[0];
  };

  getAnswers = async (constructionCardId, abortSignal) => {
    const result = await this.apiService.get(
      `/construction_cards/${constructionCardId}/answers`,
      undefined,
      { signal: abortSignal },
    );

    return result.data.data.items;
  };

  getAnswer = async (answerId) => {
    const result = await this.apiService.get(
      `/construction_cards/answers/${answerId}`,
    );

    return result.data.data.items[0];
  };

  async getComments(constructionCardId) {
    const result = await this.apiService.get(
      `/construction_cards/${constructionCardId}/comments`,
    );

    return result.data.data.items;
  }

  async createComment(constructionCardId, comment) {
    const result = await this.apiService.post(
      `/construction_cards/${constructionCardId}/comments`,
      {
        content: comment,
      },
    );

    return result.data.data.items;
  }
}

decorate(inject(SERVICE_IDENTIFIER.ApiService), ConstructionService, 0);

export default ConstructionService;
